import { useMemo } from 'react';
import {
  formatAddressShort,
  formatTime,
  isSameLocalDay,
  JOB_STATUS_LABELS,
  layoutOverlaps,
  type Job,
} from '@fsm/shared';
import { cx } from '../ui';
import {
  BOARD,
  BOARD_HEIGHT,
  formatHourLabel,
  HOUR_HEIGHT,
  HOURS,
  timeToOffset,
  WORKING_END_HOUR,
  WORKING_START_HOUR,
} from './constants';
import type { BoardColumn, BoardJob } from './types';
import type { useBoardDrag } from './useBoardDrag';

/**
 * Status colour, applied to a rail and a dot — not to the whole card.
 *
 * The board used to fill each block with a pastel wash of its status colour.
 * With seven statuses on screen at once that turns a schedule into a swatch
 * book: everything is coloured, so nothing is. Here the card stays white and
 * the colour lives in a 3px rail and a 5px dot, which is enough to scan by and
 * quiet enough to read through.
 *
 * One exception, and it is the point of the restraint: a job in progress gets
 * a genuine wash. It is the only state that means "right now", and on a board
 * where nothing else is tinted it is visible from across the room.
 */
const STATUS_STYLES: Record<string, { rail: string; dot: string; wash?: string }> = {
  unscheduled: { rail: 'bg-slate-400', dot: 'bg-slate-400' },
  scheduled: { rail: 'bg-blue-500', dot: 'bg-blue-500' },
  in_progress: { rail: 'bg-amber-500', dot: 'bg-amber-500', wash: 'bg-amber-50/80' },
  completed: { rail: 'bg-green-600', dot: 'bg-green-600' },
  invoiced: { rail: 'bg-violet-500', dot: 'bg-violet-500' },
  paid: { rail: 'bg-emerald-600', dot: 'bg-emerald-600' },
  cancelled: { rail: 'bg-red-400', dot: 'bg-red-400' },
};

type Drag = ReturnType<typeof useBoardDrag>;

export interface BoardProps {
  columns: BoardColumn[];
  jobs: Job[];
  /** Which column a job currently belongs to; null hides it from this view. */
  columnKeyFor: (job: Job) => string | null;
  /** Drag session owned by the page, so the backlog rail can share it. */
  drag: Drag;
  emptyMessage?: string;
}

export function Board({ columns, jobs, columnKeyFor, drag, emptyMessage }: BoardProps) {
  const { boardRef, drag: dragState, dragConflicts, handlers } = drag;

  /** Group jobs into columns, laying overlapping ones out side by side. */
  const byColumn = useMemo(() => {
    const map = new Map<string, BoardJob[]>();
    for (const column of columns) map.set(column.key, []);

    for (const job of jobs) {
      if (!job.scheduledStart || !job.scheduledEnd) continue;
      const key = columnKeyFor(job);
      if (!key || !map.has(key)) continue;
      map.get(key)!.push({
        ...job,
        start: new Date(job.scheduledStart),
        end: new Date(job.scheduledEnd),
      });
    }

    const out = new Map<string, Array<BoardJob & { column: number; columns: number }>>();
    for (const [key, items] of map) out.set(key, layoutOverlaps(items));
    return out;
  }, [columns, jobs, columnKeyFor]);

  const nowOffset = useMemo(() => {
    const now = new Date();
    if (now.getHours() < BOARD.dayStartHour || now.getHours() > BOARD.dayEndHour) return null;
    return timeToOffset(now);
  }, []);

  const shown = [...byColumn.values()].reduce((sum, items) => sum + items.length, 0);
  const anyColumnIsToday = columns.some((column) => isSameLocalDay(column.day, new Date()));

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {/* Column headers stay put while the hours scroll. */}
      <div className="flex shrink-0 border-b border-line bg-surface pr-[10px]">
        <div className="w-[64px] shrink-0" />
        {columns.map((column) => {
          const count = (byColumn.get(column.key) ?? []).length;
          return (
            <div
              key={column.key}
              className={cx(
                'flex min-w-0 flex-1 items-center gap-2.5 px-3.5 py-2.5',
                // A hairline between columns rather than a border on each:
                // the same separation, half the lines.
                'border-l border-line first:border-l-0',
                column.muted && 'bg-raised',
              )}
            >
              {column.color && (
                <span
                  className="size-2 shrink-0 rounded-full ring-2 ring-white"
                  style={{ background: column.color }}
                  aria-hidden
                />
              )}
              <div className="min-w-0">
                <div className="truncate text-[13px] leading-[1.25] font-semibold tracking-[-0.01em] text-ink">
                  {column.label}
                </div>
                {column.sublabel && (
                  <div className="truncate text-[11px] leading-tight tnum text-ink-4">
                    {column.sublabel}
                  </div>
                )}
              </div>
              {/*
               * The count is what a dispatcher actually reads off this row —
               * how loaded is this van — so it gets a chip rather than being
               * the faintest thing in the header.
               */}
              <span
                className={cx(
                  'ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[11px] leading-none font-semibold tnum',
                  count === 0 ? 'bg-canvas text-ink-4' : 'bg-accent-soft text-accent-2',
                )}
                title={`${count} job${count === 1 ? '' : 's'}`}
              >
                {count}
              </span>
            </div>
          );
        })}
      </div>

      {/* pt-2 so the first hour label, which sits above its row, is not clipped */}
      <div className="relative min-h-0 flex-1 overflow-y-auto pt-2">
        <div ref={boardRef} className="flex" style={{ height: BOARD_HEIGHT + 12 }}>
          <div className="relative w-[64px] shrink-0">
            {HOURS.slice(0, -1).map((hour) => {
              const working = hour >= WORKING_START_HOUR && hour < WORKING_END_HOUR;
              return (
                <div key={hour} className="relative pr-3 text-right" style={{ height: HOUR_HEIGHT }}>
                  <span
                    className={cx(
                      'absolute -top-[7px] right-3 text-[11px] leading-none tnum',
                      // Hours outside the working day are still labelled, just
                      // not competing with the ones that matter.
                      working ? 'text-ink-3' : 'text-ink-4/70',
                    )}
                  >
                    {formatHourLabel(hour)}
                  </span>
                </div>
              );
            })}

            {/* The current time, once, beside the ruler rather than on every column. */}
            {nowOffset !== null && anyColumnIsToday && (
              <div
                className="pointer-events-none absolute right-2 z-30 -translate-y-1/2"
                style={{ top: nowOffset }}
              >
                <span className="rounded-[4px] bg-[#d92d20] px-1.5 py-0.5 text-[10.5px] leading-none font-semibold tnum text-white">
                  {formatTime(new Date())}
                </span>
              </div>
            )}
          </div>

          {columns.map((column) => {
            const items = byColumn.get(column.key) ?? [];
            const showsNow = isSameLocalDay(column.day, new Date());

            return (
              <div
                key={column.key}
                data-column-key={column.key}
                className={cx(
                  'hour-grid relative min-w-0 flex-1 border-l border-line first:border-l-0',
                  column.muted && 'bg-raised/60',
                  dragState?.ghostColumnKey === column.key && 'bg-accent-soft/40',
                )}
                style={
                  {
                    height: BOARD_HEIGHT,
                    '--hour-height': `${HOUR_HEIGHT}px`,
                  } as React.CSSProperties
                }
              >
                {/*
                 * Off-hours shading, drawn behind everything. Two bands rather
                 * than a shorter board: work does occasionally start at seven,
                 * and a board that cannot show it is worse than one that shows
                 * it quietly.
                 */}
                <div
                  className="pointer-events-none absolute inset-x-0 top-0 bg-offhours"
                  style={{ height: (WORKING_START_HOUR - BOARD.dayStartHour) * HOUR_HEIGHT }}
                  aria-hidden
                />
                <div
                  className="pointer-events-none absolute inset-x-0 bottom-0 bg-offhours"
                  style={{ height: (BOARD.dayEndHour - WORKING_END_HOUR) * HOUR_HEIGHT }}
                  aria-hidden
                />

                {showsNow && nowOffset !== null && (
                  <div
                    className="pointer-events-none absolute right-0 left-0 z-20 border-t border-[#d92d20]"
                    style={{ top: nowOffset }}
                  />
                )}

                {items.map((job) => (
                  <JobBlock
                    key={job.id}
                    job={job}
                    laneIndex={job.column}
                    laneCount={job.columns}
                    dragging={dragState?.jobId === job.id}
                    handlers={handlers}
                  />
                ))}

                {/* Snapped drop preview, drawn in the target column. */}
                {dragState?.target && dragState.ghostColumnKey === column.key && (
                  <div
                    className={cx(
                      'pointer-events-none absolute inset-x-1 z-40 rounded-md border-2 border-dashed',
                      dragConflicts
                        ? 'border-red-500 bg-red-100/70'
                        : 'border-accent bg-accent-soft/80',
                    )}
                    style={{ top: dragState.ghostTop, height: Math.max(dragState.height, 20) }}
                  >
                    <div
                      className={cx(
                        'px-2 pt-1 text-[11.5px] font-semibold tnum',
                        dragConflicts ? 'text-red-700' : 'text-accent-2',
                      )}
                    >
                      {formatTime(dragState.target.start)}
                      {dragConflicts && ' · double-booked'}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {shown === 0 && emptyMessage && (
        <div className="border-t border-line bg-surface px-6 py-3 text-center text-[13px] text-ink-3">
          {emptyMessage}
        </div>
      )}
    </div>
  );
}

function JobBlock({
  job,
  laneIndex,
  laneCount,
  dragging,
  handlers,
}: {
  job: BoardJob;
  laneIndex: number;
  laneCount: number;
  dragging: boolean;
  handlers: Drag['handlers'];
}) {
  const top = timeToOffset(job.start);
  const height = Math.max(((job.end.getTime() - job.start.getTime()) / 3_600_000) * HOUR_HEIGHT, 22);
  const style = STATUS_STYLES[job.status] ?? STATUS_STYLES['scheduled']!;
  const widthPct = 100 / laneCount;
  // Three densities rather than two. A 30-minute block that tries to show
  // three lines shows three clipped lines; better to drop to the one thing
  // worth knowing at that size.
  // 50px is the threshold deliberately: an hour-long job is 56px and is by far
  // the most common block on the board, so it has to be on the side of the
  // line that shows the time and the address.
  const density = height < 32 ? 'tiny' : height < 50 ? 'short' : 'full';

  return (
    <article
      onPointerDown={(e) => handlers.onPointerDown(e, job)}
      onPointerMove={handlers.onPointerMove}
      onPointerUp={handlers.onPointerUp}
      onPointerCancel={handlers.onPointerCancel}
      className={cx(
        'no-select absolute z-10 flex cursor-grab overflow-hidden rounded-[7px] bg-surface',
        'shadow-[0_1px_1px_rgb(13_17_23/0.04),0_0_0_1px_rgb(13_17_23/0.07)]',
        'transition-shadow duration-150',
        'hover:shadow-[0_4px_12px_-2px_rgb(13_17_23/0.12),0_0_0_1px_rgb(31_86_201/0.35)]',
        dragging && 'dragging opacity-95',
        job.status === 'cancelled' && 'opacity-55',
      )}
      style={{
        top,
        height,
        left: `calc(${laneIndex * widthPct}% + 3px)`,
        width: `calc(${widthPct}% - 6px)`,
      }}
      title={`#${job.number} ${job.title} — ${JOB_STATUS_LABELS[job.status]}`}
    >
      <span className={cx('w-[3px] shrink-0', style.rail)} aria-hidden />
      <div className={cx('min-w-0 flex-1 px-2.5 py-1.5', style.wash)}>
        <div className="flex min-w-0 items-center gap-1.5">
          <span
            className={cx('size-[5px] shrink-0 rounded-full', style.dot)}
            aria-hidden
          />
          <span className="truncate text-[12.5px] leading-[1.25] font-semibold tracking-[-0.005em] text-ink">
            {job.title}
          </span>
        </div>
        {density !== 'tiny' && (
          <div className="mt-[3px] truncate text-[11.5px] leading-tight text-ink-2">
            {job.customer?.name}
          </div>
        )}
        {density === 'full' && (
          <div className="mt-[3px] truncate text-[11px] leading-tight tnum text-ink-4">
            {formatTime(job.start)} · {formatAddressShort(job.property)}
          </div>
        )}
      </div>
    </article>
  );
}
